// src/engine/gemini.ts — the BYOK real backend. Talk-mode transcription+translation and
// TTS go direct browser→Google with the user's key (matches the demo; in BYOK this is more
// private than proxying content through the operator). Listen mode (Live WebSocket) is
// wired in Phase 3 via LiveClient.
import { GOOGLE_BASE, LIVE_INPUT_HZ, TEXT_MODEL, TTS_MODEL } from "../config";
import { pcm16kToWavBase64 } from "../audio/wav";
import { playPcm24kBase64 } from "../audio/playback";
import type { LangCode, LanguagePair } from "../core/models";
import type {
  AudioClip,
  ListenCallbacks,
  ListenHandle,
  ListenOptions,
  TranscribeTranslateResult,
  TranslationEngine,
} from "./types";
import { LiveClient } from "./live";

export class GeminiEngine implements TranslationEngine {
  readonly hasRealBackend = true;
  constructor(private apiKey: string) {}

  // Live listen: connect the Live session (via @google/genai SDK) with the BYOK key,
  // stream the foreign speaker's mic, and surface transcripts. The mode selects the model.
  startListen(pair: LanguagePair, cb: ListenCallbacks, opts?: ListenOptions): ListenHandle {
    const client = new LiveClient(
      this.apiKey,
      pair,
      cb,
      opts?.mode ?? "interpret",
      opts?.getCapture
    );
    client.start();
    return {
      stop: () => client.stop(),
      setFeeding: (on) => client.setFeeding(on),
    };
  }

  async transcribeAndTranslate(
    clip: AudioClip,
    pair: LanguagePair
  ): Promise<TranscribeTranslateResult> {
    const url = `${GOOGLE_BASE}/v1beta/models/${TEXT_MODEL}:generateContent?key=${encodeURIComponent(
      this.apiKey
    )}`;
    const wav = pcm16kToWavBase64(new Int16Array(clip.pcm16k), LIVE_INPUT_HZ);
    const sys =
      `You transcribe then translate a short spoken utterance. The audio is in ${pair.native}. ` +
      `Return ONLY minified JSON: {"sourceText":"<verbatim ${pair.native} transcript>",` +
      `"targetText":"<${pair.foreign} translation, no quotes or commentary>"}. ` +
      `Preserve meaning, tone, and register.`;
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        system_instruction: { parts: [{ text: sys }] },
        contents: [
          { role: "user", parts: [{ inline_data: { mime_type: "audio/wav", data: wav } }] },
        ],
        generationConfig: { temperature: 0.2, responseMimeType: "application/json" },
      }),
    });
    if (!r.ok) throw new Error("transcribe/translate failed: " + r.status);
    const j = await r.json();
    const rawText = (j?.candidates?.[0]?.content?.parts?.[0]?.text ?? "{}").trim();
    let parsed: { sourceText?: string; targetText?: string } = {};
    try {
      parsed = JSON.parse(rawText);
    } catch {
      // Model returned prose despite the schema; treat the whole thing as the translation.
      parsed = { sourceText: "", targetText: rawText };
    }
    return {
      sourceText: (parsed.sourceText ?? "").trim(),
      targetText: (parsed.targetText ?? "").trim(),
    };
  }

  async translateText(text: string, src: LangCode, dst: LangCode): Promise<string> {
    const url = `${GOOGLE_BASE}/v1beta/models/${TEXT_MODEL}:generateContent?key=${encodeURIComponent(
      this.apiKey
    )}`;
    const sys =
      `You are a translation engine. Translate the user's ${src} text into ${dst}. ` +
      `Output only the ${dst} translation, no quotes or commentary.`;
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        system_instruction: { parts: [{ text: sys }] },
        contents: [{ role: "user", parts: [{ text }] }],
        generationConfig: { temperature: 0.2 },
      }),
    });
    if (!r.ok) throw new Error("translate failed: " + r.status);
    const j = await r.json();
    return (j?.candidates?.[0]?.content?.parts?.[0]?.text ?? "").trim();
  }

  async speak(text: string, voice: string, _lang: LangCode, volume: number): Promise<void> {
    // Native Gemini TTS is a generateContent call with an AUDIO response modality and a
    // speechConfig. The audio comes back as base64 PCM 24 kHz/16-bit mono in inlineData.
    const url = `${GOOGLE_BASE}/v1beta/models/${TTS_MODEL}:generateContent?key=${encodeURIComponent(
      this.apiKey
    )}`;
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents: [{ parts: [{ text }] }],
        generationConfig: {
          responseModalities: ["AUDIO"],
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: voice } } },
        },
      }),
    });
    if (!r.ok) {
      const detail = await r.text().catch(() => "");
      throw new Error(`TTS ${r.status}: ${detail.slice(0, 300)}`); // surfaced by caller
    }
    const j = await r.json();
    const parts: Array<{ inlineData?: { data?: string } }> =
      j?.candidates?.[0]?.content?.parts ?? [];
    const b64 = parts.find((p) => p.inlineData?.data)?.inlineData?.data;
    if (!b64) throw new Error("TTS returned no audio");
    await playPcm24kBase64(b64, volume);
  }
}
